import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, UserCog, CheckCircle, Clock, FileText, ListChecks, Trophy, Sigma, Sliders, RefreshCw, Save, ChevronDown, ChevronUp, Calendar } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

export default function PeriodeDetailPage() {
  const { periodeId } = useParams();
  const [data, setData] = useState(null);
  const [hasil, setHasil] = useState(null);
  const [tab, setTab] = useState("info");
  const [indikator, setIndikator] = useState([]);
  const [defaultFixedZero, setDefaultFixedZero] = useState([]);
  const [showObsConfig, setShowObsConfig] = useState(false);
  const [obsEdit, setObsEdit] = useState({ jml_obs: [], fixed_zero: [] });
  const [meta, setMeta] = useState({ bulan: "", tahun: "" });

  const load = async () => {
    try {
      const r = await api.get(`/admin/periode/${periodeId}`);
      setData(r.data);
      setObsEdit({
        jml_obs: r.data.jml_obs || [],
        fixed_zero: r.data.fixed_zero || [],
      });
      setMeta({ bulan: r.data.bulan || "", tahun: r.data.tahun || "" });
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  useEffect(() => {
    load();
    api.get(`/admin/periode/${periodeId}/hasil`).then((r) => setHasil(r.data)).catch(() => {});
    api.get("/constants").then((r) => {
      setIndikator(r.data.indikator_observasi || []);
      setDefaultFixedZero(r.data.default_fixed_zero || []);
    });
  }, [periodeId]);

  const toggleStatus = async () => {
    const newStatus = data.status === "open" ? "closed" : "open";
    try {
      await api.put(`/admin/periode/${periodeId}`, { status: newStatus });
      setData({ ...data, status: newStatus });
      toast.success(`Periode ${newStatus === "open" ? "dibuka" : "ditutup"}`);
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const resetObsDefault = () => {
    const defFz = new Set(defaultFixedZero);
    setObsEdit({
      jml_obs: indikator.map((i) => i.jml_obs),
      fixed_zero: indikator.map((i) => defFz.has(i.no)),
    });
    toast.success("Direset ke default. Klik 'Simpan' untuk menerapkan.");
  };

  const saveObsConfig = async () => {
    try {
      await api.put(`/admin/periode/${periodeId}`, {
        jml_obs: obsEdit.jml_obs,
        fixed_zero: obsEdit.fixed_zero,
      });
      toast.success("Pengaturan JML OBS & SELALU 0 disimpan");
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const saveMeta = async () => {
    try {
      await api.put(`/admin/periode/${periodeId}`, {
        bulan: meta.bulan,
        tahun: meta.tahun,
      });
      toast.success("Bulan & Tahun tanda tangan disimpan");
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  if (!data) return <div className="p-4 md:p-8 text-slate-500">Memuat...</div>;

  return (
    <div className="p-4 md:p-8">
      <Link to="/admin/periode" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 mb-4"><ArrowLeft className="w-4 h-4" /> Kembali ke daftar jadwal</Link>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Detail Periode</div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">{data.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{data.nomor}</h1>
          <p className="text-slate-500 mt-1 text-sm">{data.bulan} {data.tahun} • Pengasuh: {data.pengasuh?.nama}</p>
        </div>
        <button onClick={toggleStatus} className={`px-4 py-2 rounded-sm text-sm font-semibold ${data.status === "open" ? "bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200" : "bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200"}`}>
          {data.status === "open" ? "Tutup Periode" : "Buka Periode"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <Users className="w-4 h-4 text-brass-500 mb-2" />
          <div className="text-2xl font-bold font-mono text-navy-900">{data.total_count}</div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Serdik dinilai</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <ListChecks className="w-4 h-4 text-blue-500 mb-2" />
          <div className="text-2xl font-bold font-mono text-navy-900">{data.submitted_count}/{data.total_count}</div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Sudah mengisi</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <UserCog className="w-4 h-4 text-emerald-500 mb-2" />
          <div className="text-sm font-bold text-navy-900 truncate">{data.pengasuh?.nama}</div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Pengasuh</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          {data.status === "open" ? <Clock className="w-4 h-4 text-amber-500 mb-2" /> : <CheckCircle className="w-4 h-4 text-slate-500 mb-2" />}
          <div className="text-sm font-bold uppercase">{data.status === "open" ? "Aktif" : "Ditutup"}</div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Status</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-6">
        <h3 className="font-heading font-bold text-navy-900 mb-3">Daftar Serdik & Status Pengisian</h3>
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="periode-detail-siswa-list">
          <thead>
            <tr className="bg-slate-50 text-slate-700 text-xs uppercase tracking-wide">
              <th className="text-left p-2 w-12">#</th>
              <th className="text-left p-2">Nama</th>
              <th className="text-left p-2 w-32">NOSIS</th>
              <th className="text-center p-2 w-24">Ranking</th>
              <th className="text-center p-2 w-32">Sosiometri</th>
            </tr>
          </thead>
          <tbody>
            {data.siswa.map((s, i) => {
              const rank = data.ranks?.[s.id];
              const hasSubmitted = (hasil?.hasil || []).find((h) => h.siswa.id === s.id);
              const sosioFilled = hasSubmitted ? (hasil?.hasil || []).find((h) => h.raters?.a?.id === s.id) : null;
              return (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="p-2 text-slate-500 font-mono">{i + 1}</td>
                  <td className="p-2 font-medium text-navy-900">{s.nama}</td>
                  <td className="p-2 font-mono text-slate-600">{s.nosis}</td>
                  <td className="p-2 text-center">{rank ? <span className="inline-block px-2 py-0.5 bg-brass-100 border border-brass-300 text-brass-800 rounded-sm font-mono font-bold">{rank}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="p-2 text-center"><span className="text-xs text-slate-500">Lihat di tab Hasil</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Bulan & Tahun Tanda Tangan (per-periode) */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 md:p-6 mt-6">
        <h3 className="font-heading font-bold text-navy-900 mb-1 flex items-center gap-2"><Calendar className="w-4 h-4 text-brass-500" /> Bulan &amp; Tahun Tanda Tangan</h3>
        <p className="text-xs text-slate-500 mb-4">Nilai ini akan tampil pada blok tanda tangan (TTD) di dokumen Sosiometri, Catatan Siku, dan Nilai Gabungan untuk periode ini.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Bulan</label>
            <input
              value={meta.bulan}
              onChange={(e) => setMeta({ ...meta, bulan: e.target.value })}
              data-testid="periode-meta-bulan"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Tahun</label>
            <input
              value={meta.tahun}
              onChange={(e) => setMeta({ ...meta, tahun: e.target.value })}
              data-testid="periode-meta-tahun"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
            />
          </div>
          <button
            onClick={saveMeta}
            data-testid="periode-meta-save"
            className="bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 h-fit"
          ><Save className="w-4 h-4" /> Simpan</button>
        </div>
      </div>

      {/* JML OBS & Toggle SELALU 0 (per-periode) */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
        <button
          type="button"
          onClick={() => setShowObsConfig((x) => !x)}
          data-testid="periode-detail-obs-toggle"
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-brass-500" />
            <div>
              <h3 className="font-heading font-bold text-navy-900">JML OBS &amp; Toggle SELALU 0 (59 indikator)</h3>
              <p className="text-xs text-slate-500 mt-0.5">Pengaturan yang tersimpan pada periode ini. Perubahan akan diterapkan pada perhitungan Catatan Siku periode ini saja.</p>
            </div>
          </div>
          {showObsConfig ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>
        {showObsConfig && (
          <div className="mt-4">
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={resetObsDefault}
                data-testid="periode-detail-obs-reset"
                className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"
              ><RefreshCw className="w-3 h-3" /> Reset ke Default</button>
            </div>
            <div className="overflow-auto max-h-[500px] border border-slate-200 rounded-sm">
              <table className="w-full text-sm" data-testid="periode-detail-obs-table">
                <thead className="sticky top-0">
                  <tr className="bg-slate-100 text-xs uppercase">
                    <th className="p-2 w-12 text-center">No</th>
                    <th className="p-2 text-left">Indikator</th>
                    <th className="p-2 w-16 text-center">Kode</th>
                    <th className="p-2 w-24 text-center">JML OBS</th>
                    <th className="p-2 w-24 text-center">SELALU 0</th>
                  </tr>
                </thead>
                <tbody>
                  {indikator.map((ind, i) => (
                    <tr key={ind.no} className={`border-t border-slate-100 ${obsEdit.fixed_zero?.[i] ? "bg-amber-50" : ""}`}>
                      <td className="p-2 text-center font-mono text-slate-500">{ind.no}</td>
                      <td className="p-2 text-xs">{ind.teks}</td>
                      <td className="p-2 text-center font-mono font-bold text-navy-900">{ind.kode}</td>
                      <td className="p-2 text-center">
                        <input
                          type="number" min="0"
                          value={obsEdit.jml_obs?.[i] ?? ind.jml_obs}
                          data-testid={`periode-detail-obs-jml-${ind.no}`}
                          onChange={(e) => {
                            const next = [...(obsEdit.jml_obs || indikator.map((x) => x.jml_obs))];
                            next[i] = Number(e.target.value) || 0;
                            setObsEdit({ ...obsEdit, jml_obs: next });
                          }}
                          className="w-16 px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!obsEdit.fixed_zero?.[i]}
                          data-testid={`periode-detail-obs-fz-${ind.no}`}
                          onChange={(e) => {
                            const next = [...(obsEdit.fixed_zero || indikator.map(() => false))];
                            next[i] = e.target.checked;
                            setObsEdit({ ...obsEdit, fixed_zero: next });
                          }}
                          className="w-4 h-4 accent-brass-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={saveObsConfig}
              data-testid="periode-detail-obs-save"
              className="mt-4 bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"
            ><Save className="w-4 h-4" /> Simpan JML OBS</button>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/admin/sosiometri" className="bg-white border border-slate-300 hover:border-brass-500 text-navy-900 px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4" /> Lihat Hasil Sosiometri
        </Link>
        <Link to="/admin/observasi" className="bg-white border border-slate-300 hover:border-brass-500 text-navy-900 px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
          <ListChecks className="w-4 h-4" /> Lihat Hasil Observasi
        </Link>
        <Link to="/admin/nilai-gabungan" className="bg-white border border-slate-300 hover:border-brass-500 text-navy-900 px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
          <Sigma className="w-4 h-4" /> Nilai Gabungan
        </Link>
        <Link to="/admin/ranking" className="bg-white border border-slate-300 hover:border-brass-500 text-navy-900 px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
          <Trophy className="w-4 h-4" /> Ranking
        </Link>
      </div>
    </div>
  );
}
